import { Platform, StyleSheet } from 'react-native';
import type { Theme } from '../../../src/theme/getTheme';
import {
  COMPETITION_WELCOME_CIRCLE_COUNT,
  COMPETITION_WELCOME_ROOT_ELEVATION,
  COMPETITION_WELCOME_ROOT_Z_INDEX,
  COMPETITION_WELCOME_SKIP_Z_INDEX,
} from './CompetitionWelcomeScreen.constants';

export interface CompetitionWelcomeInsets {
  top: number;
  bottom: number;
  right: number;
}

export const getCompetitionWelcomeStyles = (
  theme: Theme,
  insets: CompetitionWelcomeInsets,
) =>
  StyleSheet.create({
    /**
     * Full-screen takeover. The competition shell header is `zIndex: 200` / `elevation: 200`
     * (see `appStyles/_worldcup.styles.ts`), so the intro must outrank it or the header
     * paints through and its menu icon collides with Skip. Web uses `position: fixed`
     * for the same reason the WC intro does: an ancestor stacking context can otherwise
     * clip an absolutely-positioned overlay.
     */
    root: {
      ...StyleSheet.absoluteFillObject,
      backgroundColor: theme.background.primary,
      zIndex: COMPETITION_WELCOME_ROOT_Z_INDEX,
      elevation: COMPETITION_WELCOME_ROOT_ELEVATION,
      flexDirection: 'column',
      ...(Platform.OS === 'web' && {
        position: 'fixed' as never,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100%' as never,
      }),
    },
    phaseLayer: {
      flex: 1,
    },
    /**
     * Skip lives outside the scrolling body (it used to be the first row *inside* the
     * ScrollView, which put it under the shell header's menu icon and scrolled it away).
     */
    skipWrap: {
      position: 'absolute',
      top: insets.top + 4,
      right: insets.right + 4,
      zIndex: COMPETITION_WELCOME_SKIP_Z_INDEX,
    },
    skipPressable: {
      paddingVertical: theme.spacing.sm,
      paddingHorizontal: theme.spacing.md,
      borderRadius: theme.borderRadius.md,
      ...(Platform.OS === 'web' && { cursor: 'pointer' as never }),
    },
    skipPressablePressed: { opacity: 0.6 },
    skipPressableHovered: { opacity: 0.85 },
    skipLabel: { fontSize: 14, fontWeight: '600', color: theme.gray[500] },
  });

/** Shared phase-body styles (rules + points scroll bodies and the splash column). */
export const getCompetitionWelcomePhaseStyles = (
  theme: Theme,
  opts: { scrollContentPaddingTop: number; bottomInset: number },
) =>
  StyleSheet.create({
    /**
     * Column: a bounded scroll body over a pinned footer. Web needs the explicit
     * `minHeight: 0` — the default `auto` lets the flex child grow to its content,
     * which un-bounds the ScrollView and pushes the footer off screen.
     */
    container: {
      flex: 1,
      ...(Platform.OS === 'web' && { minHeight: 0 as never, flexBasis: 0 as never }),
    },
    scroll: {
      flex: 1,
      ...(Platform.OS === 'web' && { minHeight: 0 as never }),
    },
    scrollContent: {
      paddingTop: opts.scrollContentPaddingTop,
      paddingBottom: 24,
      paddingHorizontal: 20,
      flexGrow: 1,
    },
    /**
     * The CTA is pinned, not the last row of the scroll body: inside the ScrollView it
     * only appeared after reading to the bottom, and it landed under the root tab bar.
     * `bottomInset` lives here now — the scroll body no longer needs to reserve it.
     */
    footer: {
      paddingHorizontal: 20,
      paddingTop: 12,
      paddingBottom: opts.bottomInset + 12,
      borderTopWidth: StyleSheet.hairlineWidth,
      borderTopColor: theme.gray[200] + '85',
      backgroundColor: theme.background.primary,
    },
    eyebrow: {
      fontSize: 12,
      fontWeight: '700',
      letterSpacing: 1.4,
      textTransform: 'uppercase',
      color: theme.accent,
      marginBottom: 6,
    },
    title: { fontSize: 26, fontWeight: '700', color: theme.black, marginBottom: 8 },
    subtitle: {
      fontSize: theme.typography.bodySmall.fontSize,
      color: theme.gray[500],
      lineHeight: 20,
      marginBottom: 18,
    },

    /** Sketch-framed rule card. The frame inks the border, so the card itself is borderless. */
    card: {
      backgroundColor: theme.background.card,
      borderRadius: 20,
      padding: 16,
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: 12,
    },
    cardSpacer: { marginBottom: 14 },
    cardIconWrap: {
      width: 38,
      height: 38,
      borderRadius: 19,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.background.inset,
    },
    cardBody: { flex: 1 },
    ruleTitle: { fontSize: 15, fontWeight: '700', color: theme.black, marginBottom: 4 },
    /**
     * `gray[600]` (not 400): in this dark-only theme the scale runs dark→light, so
     * `gray[400]` (#6B7280) is a *mid* grey that lands at 3.0:1 on the card — under
     * WCAG AA. 600 is the theme's documented "primary text on dark" and matches the
     * WC rules phase.
     */
    ruleDesc: {
      fontSize: theme.typography.bodySmall.fontSize,
      color: theme.gray[600],
      lineHeight: 20,
    },

    // The flat point-table row styles (`pointsRow` / `pointsCell` / `pointsHead` /
    // `pointsValue` / `pointsStage`) were removed with Story 13: the point beat now draws the
    // World Cup's stage timeline and takes its geometry and colours from
    // `getWelcomePointsPhaseStyles`, so nothing here would keep them honest.

    ctaButton: {
      backgroundColor: theme.primary,
      borderRadius: 22,
      paddingVertical: 14,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 6,
      ...(Platform.OS === 'web' && { cursor: 'pointer' as never }),
    },
    ctaButtonPressed: { opacity: 0.85 },
    ctaButtonHovered: { opacity: 0.9 },
    ctaLabel: { fontSize: 16, fontWeight: '700', color: theme.onPrimary },

    dots: { flexDirection: 'row', justifyContent: 'center', gap: 6, marginTop: 10 },
    dot: { width: 7, height: 7, borderRadius: 4, backgroundColor: theme.gray[300] },
    dotActive: { backgroundColor: theme.accent, width: 18 },
  });

/** Splash-only: mascot hero, glow dome and the pulsing rings behind it. */
export const getCompetitionWelcomeSplashStyles = (
  theme: Theme,
  opts: { topInset: number; bottomInset: number },
) =>
  StyleSheet.create({
    container: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      paddingTop: opts.topInset + 24,
      paddingBottom: opts.bottomInset + 24,
      paddingHorizontal: 24,
    },
    heroWrap: {
      width: 200,
      height: 200,
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: 8,
    },
    glow: { position: 'absolute' },
    circlesLayer: { ...StyleSheet.absoluteFillObject, alignItems: 'center', justifyContent: 'center' },
    mascot: { width: 132, height: 132, resizeMode: 'contain' },
    eyebrow: {
      fontSize: 12,
      fontWeight: '700',
      letterSpacing: 1.4,
      textTransform: 'uppercase',
      color: theme.accent,
      marginBottom: 8,
      textAlign: 'center',
    },
    title: {
      fontSize: 30,
      fontWeight: '800',
      color: theme.black,
      marginBottom: 10,
      textAlign: 'center',
    },
    tagline: {
      fontSize: theme.typography.body.fontSize,
      color: theme.gray[500],
      lineHeight: 24,
      marginBottom: 28,
      textAlign: 'center',
    },
    button: {
      backgroundColor: theme.primary,
      borderRadius: 22,
      paddingVertical: 14,
      paddingHorizontal: 32,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 6,
      ...(Platform.OS === 'web' && { cursor: 'pointer' as never }),
    },
    buttonPressed: { opacity: 0.85 },
    buttonHovered: { opacity: 0.9 },
    buttonLabel: { fontSize: 16, fontWeight: '700', color: theme.onPrimary },
    dots: { flexDirection: 'row', justifyContent: 'center', gap: 6, marginTop: 22 },
    dot: { width: 7, height: 7, borderRadius: 4, backgroundColor: theme.gray[300] },
    dotActive: { backgroundColor: theme.accent, width: 18 },
  });

/** Concentric pulse rings, largest first so the smallest paints on top. */
export const getCompetitionWelcomeCircleStyles = (theme: Theme) =>
  Array.from({ length: COMPETITION_WELCOME_CIRCLE_COUNT }, (_, i) => {
    const size = 200 - i * 42;
    return {
      position: 'absolute' as const,
      width: size,
      height: size,
      borderRadius: size / 2,
      borderWidth: 1.5,
      borderColor: theme.primaryLight,
    };
  });
